<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Plus } from 'lucide-vue-next'
import TemplatesList from './TemplatesList.vue'
import CreateTemplateDialog from '@/pages/Project/Templates/CreateTemplateDialog.vue'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import type { AdminTemplate } from '@/types/admin'

const router = useRouter()
const store = useStore()
const member = computed(() => store.getters.getMember)

const templates = ref<AdminTemplate[]>([])
const loading = ref(false)
const searchQuery = ref('')
const limit = ref(100)
const offset = ref(0)
const total = ref(0)
let debounceTimer: NodeJS.Timeout | null = null

const filteredTemplates = computed(() => {
  if (!searchQuery.value) {
    return templates.value
  }
  return templates.value.filter(template =>
    template.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const fetchTemplates = async () => {
  loading.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/templates`,
      params: {
        limit: limit.value,
        offset: offset.value,
        search: searchQuery.value || undefined
      }
    })
    templates.value = response.data.items
    total.value = response.data.total
  } catch (error) {
    toast({
      title: 'Error',
      description: `Failed to fetch templates: ${error instanceof Error ? error.message : String(error)}`,
      variant: 'destructive'
    })
  } finally {
    loading.value = false
  }
}

const debouncedFetchTemplates = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  debounceTimer = setTimeout(() => {
    fetchTemplates()
  }, 300)
}

const onTemplateUpdated = () => {
  fetchTemplates()
}

onMounted(() => {
  if (!member.value?.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchTemplates()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="p-6 space-y-4 flex-1 overflow-auto">
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-bold">Templates</h1>
    </div>

    <div class="flex items-center justify-between">
      <div class="relative">
        <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          v-model="searchQuery"
          @input="debouncedFetchTemplates"
          placeholder="Search templates..."
          class="pl-10"
        />
      </div>
      <CreateTemplateDialog :project-id="undefined" @template-updated="onTemplateUpdated">
        <Button variant="default" size="sm" class="max-w-xs">
          <Plus class="mr-2 h-4 w-4" />
          New Template
        </Button>
      </CreateTemplateDialog>
    </div>

    <div class="text-sm text-gray-600">
      Showing {{ total }} template{{ total !== 1 ? 's' : '' }}
    </div>

    <TemplatesList
      :templates="filteredTemplates"
      :loading="loading"
      @template-updated="onTemplateUpdated"
    />
    </div>
  </div>
</template>
